import { map, withLatestFrom } from 'rxjs/operators';
import { Directive, TemplateRef, ViewContainerRef, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';

import * as selectors from '../../store/reducers';
import { auth } from '../../store/auth/auth.selectors';

/**
 * Has-role directive
 * Example usage:
 *
 * component.ts:
 * | const allowedRoles: string[] = [CARER_ROLE, FAMILY_ROLE]
 *
 * component.html:
 * |  <ng-container *hasRole="allowedRoles"> Content </ng-container>
 *
 * A directive to either display or hide content based on whether the user's
 * roles within the current organisation include any roles passed in.
 *
 * :Note: If the roles input is an empty array, the element is not rendered.
 */

@Directive({ selector: '[hasRole]' })
export class RoleDirective implements OnDestroy {
  roles = [];
  userRoles: string[];

  sub: any;

  constructor(
    public store: Store<any>,
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef
  ) {
    this.sub = this.store
      .select(selectors.org.currentOrgRoles)
      .pipe(
        withLatestFrom(this.store.select(auth.getRoles)),
        map((roles) => {
          const result = roles.reduce((prev, next) => prev.concat(next), []);
          this.userRoles = result;
          this._updateView();
        })
      )
      .subscribe();
  }

  @Input()
  set hasRole(roles: any) {
    if (!roles) {
      roles = [];
    }
    if (!Array.isArray(roles)) {
      roles = [roles];
    }
    this.roles = roles;
    this._updateView();
  }

  private _checkRoles(roles) {
    if (!roles.length) {
      return false;
    }
    if (!this.userRoles) {
      return false;
    }
    for (let i = 0; i < roles.length; i++) {
      const role = roles[i];
      if (this.userRoles.includes(role)) {
        return true;
      }
    }
    return false;
  }

  private _updateView() {
    const hasRole = this._checkRoles(this.roles);
    this.viewContainerRef.clear();
    if (hasRole) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
